import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { Text, ActionButton } from '@adobe/react-spectrum';
import { selectHome } from '../../pages/Home/slice/selectors';
import { useSlideoutUrl } from '../../hooks/useSlideoutUrl';
import { SlideOutPanel } from '../SlideOutPanel';
import { fetchMomentDataCached } from '../../services/momentsApi';
import type { MomentData } from '../../services/momentsApi';
import { useDispatch } from 'react-redux';
import { useCommentSlice } from '../SlideOutPanel/CommentPanel/CommentState';
import { selectCardData } from '../SlideOutPanel/CommentPanel/CommentState/selectors';
import {
  selectIsAuthenticated,
  selectProfileLoading,
  selectUserProfile
} from '../../pages/Layout/slice/selectors';
import { layoutActions } from '../../pages/Layout/slice';
import { useAlertsAndNotificationsSlice } from '../AlertsAndNotification/store/slice';

/**
 * Component that manages slideout opening based on URL paths
 * Supports both API-based data fetching and fallback to local data
 * URL structure: /moments/{issueId}
 */
export const UrlSlideoutManager: React.FC = () => {
  const dispatch = useDispatch();
  const cardDataFrmRedux = useSelector(selectCardData);
  const loggedInUser = useSelector(selectUserProfile);
  const isProfileLoading = useSelector(selectProfileLoading);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { actions } = useCommentSlice();
  const { actions: alertsActions } = useAlertsAndNotificationsSlice();
  const {
    isSlideoutOpen,
    currentIssueId,
    closeSlideout,
    currentReleasePhaseId
  } = useSlideoutUrl();
  const homeState = useSelector(selectHome);
  const [selectedCard, setSelectedCard] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiData, setApiData] = useState<MomentData | null>(null);

  // Enhanced loading states for better UX
  const [showContent, setShowContent] = useState(false);
  const [loadingStartTime, setLoadingStartTime] = useState<number | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  React.useEffect(() => {
    // Fallback profile fetch only when there is no profile and no active profile request
    if (!loggedInUser?.uid && !isProfileLoading && !isAuthenticated) {
      dispatch(layoutActions.loadUserProfile());
    }
  }, [loggedInUser?.uid, isProfileLoading, isAuthenticated, dispatch]);
  // Minimum loading display time (in ms) to prevent flickering.
  //
  // This is a floor on how long the skeleton stays up, not a fetch cost: when the moment resolves
  // faster than this, the panel deliberately waits out the remainder. It exists to stop a
  // skeleton→content flash on a fast response, so it only needs to cover a flash, and 800 ms was
  // adding most of that to every open. 150 ms keeps the anti-flicker guarantee (the transition below
  // is itself 150 ms) while letting a fast open feel fast.
  const MIN_LOADING_TIME = 150;

  // Helper function to smoothly transition from loading to content
  const transitionToContent = async (
    cardData: any,
    apiData?: MomentData | null
  ) => {
    if (!loadingStartTime) return;

    const elapsed = Date.now() - loadingStartTime;
    const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsed);

    // Wait for minimum loading time if needed
    if (remainingTime > 0) {
      await new Promise(resolve => setTimeout(resolve, remainingTime));
    }

    // Start transition
    setIsTransitioning(true);

    // Small delay for smooth transition
    setTimeout(() => {
      setSelectedCard(cardData);
      if (apiData) {
        setApiData(apiData);
      }
      setIsLoading(false);
      setShowContent(true);
      setIsTransitioning(false);
      setLoadingStartTime(null);
    }, 150);
  };

  // Function to find a card by issueId in local data as fallback
  const findCardByIssueId = (issueId: string) => {
    if (!homeState.releaseData) return null;

    // Search in main release data
    for (const release of homeState.releaseData.releaseScheduled || []) {
      for (const project of release.projects || []) {
        for (const subProject of project.project || []) {
          if (subProject.issueId === issueId) {
            return subProject;
          }

          // Search in child relationships
          if (subProject.childRelationships) {
            for (const child of subProject.childRelationships) {
              if (child.issueId === issueId) {
                return child;
              }
            }
          }
        }
      }
    }

    // Search in events data
    for (const release of homeState.releaseData.releaseEvents || []) {
      for (const project of release.projects || []) {
        for (const subProject of project.project || []) {
          if (subProject.issueId === issueId) {
            return subProject;
          }

          // Search in child relationships
          if (subProject.childRelationships) {
            for (const child of subProject.childRelationships) {
              if (child.issueId === issueId) {
                return child;
              }
            }
          }
        }
      }
    }

    return null;
  };

  // Convert API data to card format expected by SlideOutPanel
  const convertApiDataToCard = (data: MomentData) => {
    // Handle new response format with releasePhases array
    const activePhase = Array.isArray(data.releasePhases)
      ? data.releasePhases.find(phase => phase.status === 'ACTIVE') ||
        data.releasePhases[0]
      : null;

    // Extract data from either new format (releasePhases) or old format (generalInfo at root)
    const generalInfo = activePhase?.generalInfo || data.generalInfo;
    // Keep categories as array - don't convert to single value
    const categories = data.categories;
    const productGroups = Array.isArray(data.productGroups)
      ? data.productGroups[0]
      : data.productGroups;

    return {
      issueId: data.issueId,
      title: (data as any).title || data.name, // Use mapped title, fallback to name
      categories: categories,
      products:
        data.products ||
        (generalInfo && 'products' in generalInfo
          ? generalInfo.products
          : undefined),
      status:
        data.status ||
        (generalInfo && 'reportingStatus' in generalInfo
          ? generalInfo.reportingStatus
          : undefined),
      dates: generalInfo?.dates,
      labels: data.tags || [],
      riskLevel:
        generalInfo?.riskLevel ||
        (generalInfo &&
        'reportingStatus' in generalInfo &&
        generalInfo.reportingStatus?.includes('High')
          ? 'High'
          : generalInfo &&
            'reportingStatus' in generalInfo &&
            generalInfo.reportingStatus?.includes('Moderate')
          ? 'Moderate'
          : 'Low'),
      priority: generalInfo?.tier,
      projectType: data.momentType || generalInfo?.momentType,
      relationships: [],
      childRelationships: [],
      links: Array.isArray(data.additionalInfo) ? data.additionalInfo : [],
      lastUpdated: data.updated || new Date().toISOString(),
      reportingSummary:
        data.overview?.launchSummary || data.overview?.headline || '',
      // Add the full API data for the slideout to use
      _apiData: data,
      overview: {
        headlineUpdatedAt: data?.overview?.headlineUpdatedAt
      }
    };
  };

  const handleCardDataForComments = convertedCard => {
    const momentType = convertedCard.projectType || '';
    const issueId = convertedCard.issueId;
    const issueName = convertedCard?.title;
    //Call Dropdown API's iff momentType are different
    if (momentType !== cardDataFrmRedux?.momentType) {
      dispatch(actions.fetchSectionOptions(momentType));
    }
    //Update Selected Card Data
    dispatch(
      actions.setSelectedCardData({
        issueId: issueId,
        momentType: momentType,
        issueName: issueName
      })
    );
    // Always fetch badge count when card data is available
    if (issueId) {
      dispatch(actions.resetSelectedFilter());
      //need it here to refresh comment count
      dispatch(actions.fetchFilterOptions({ issueId, momentType }));
    }
  };

  // Effect to fetch data when URL changes
  useEffect(() => {
    if (isSlideoutOpen && currentIssueId) {
      setIsLoading(true);
      setError(null);
      setApiData(null);

      // Try to fetch data from API first
      // Use forceRefresh=true to ensure we always get the latest data when opening via URL
      // Also fetch moment-specific notifications in parallel
      Promise.all([
        fetchMomentDataCached(currentIssueId, true), // Force refresh for URL-based opens
        // Fetch moment-specific notifications
        Promise.resolve(
          dispatch(
            alertsActions.loadMomentNotifications({ issueId: currentIssueId })
          )
        )
      ])
        .then(([response]) => {
          if (response.success && response.data) {
            setApiData(response.data);

            // ✅ RESTRICT: Check if moment type is Key Date or Event
            const momentType = response.data.momentType;
            const isRestrictedType =
              momentType === 'Key Date' ||
              momentType === 'Event' ||
              momentType === 'key-date' ||
              momentType === 'event';

            if (isRestrictedType) {
              console.warn(
                `⚠️ Slideout restricted for moment type: ${momentType}`
              );
              setError(
                `Slideout is not available for ${momentType}s. Please view from the timeline.`
              );
              return;
            }

            // Convert and validate the card data
            try {
              const convertedCard = convertApiDataToCard(response.data);
              // IMPORTANT: Attach the API data to the card to prevent duplicate API calls
              // SlideOutPanel checks for card._apiData and skips fetching if present
              convertedCard._apiData = response.data;

              handleCardDataForComments(convertedCard);
              setSelectedCard(convertedCard);
            } catch (conversionError) {
              console.error(
                '❌ Error converting API data to card format:',
                conversionError
              );
              setError('Failed to process moment data');
            }
          } else {
            console.warn(
              '⚠️ API fetch failed, falling back to local data:',
              response.error
            );
            // Fallback to local data
            const localCard = findCardByIssueId(currentIssueId);
            if (localCard) {
              // ✅ RESTRICT: Check moment type in local data too
              const momentType = localCard.projectType;
              const isRestrictedType =
                momentType === 'Key Date' ||
                momentType === 'Event' ||
                momentType === 'key-date' ||
                momentType === 'event';

              if (isRestrictedType) {
                console.warn(
                  `⚠️ Slideout restricted for moment type: ${momentType}`
                );
                setError(
                  `Slideout is not available for ${momentType}s. Please view from the timeline.`
                );
                return;
              }

              setSelectedCard(localCard);
            } else {
              console.error(
                '❌ No local card found for issueId:',
                currentIssueId
              );
              setError(response.message || 'Moment not found');
            }
          }
        })
        .catch(err => {
          console.error('❌ Error fetching moment data:', err);
          // Fallback to local data
          const localCard = findCardByIssueId(currentIssueId);
          if (localCard) {
            setSelectedCard(localCard);
          } else {
            console.error(
              '❌ No local card found after API error for issueId:',
              currentIssueId
            );
            setError('Unable to load moment data');
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setSelectedCard(null);
      setApiData(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSlideoutOpen, currentIssueId]);
  // Note: homeState.releaseData removed from deps to prevent duplicate API calls on page refresh
  // It's only used as a fallback if API fetch fails, not as a trigger for refetching

  // Don't render anything if slideout shouldn't be open
  if (!isSlideoutOpen || !currentIssueId) {
    return null;
  }

  // Show blur overlay (no spinner) while fetching
  // Also cover transient states where data isn't ready yet but no error
  if (!selectedCard && !error) {
    return createPortal(
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          zIndex: 9999,
          transition: 'opacity 0.3s ease-out'
        }}
      />,
      document.body
    );
  }

  // Show error state
  if (error) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div
          style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            textAlign: 'center',
            maxWidth: '400px'
          }}
        >
          <Text>
            <strong>Error loading moment: {error}</strong>
          </Text>
          <ActionButton marginTop="size-300" onPress={closeSlideout}>
            Close
          </ActionButton>
        </div>
      </div>
    );
  }

  // Always render SlideOutPanel when isSlideoutOpen is true to prevent unmounting/remounting
  // This prevents the slide animation from playing when navigating between moments
  return (
    <SlideOutPanel
      key="slideout-panel" // Keep same key to prevent remounting
      card={selectedCard}
      onClose={closeSlideout}
      isOpen={isSlideoutOpen && !!selectedCard} // Only show when we have data
      releasePhaseId={currentReleasePhaseId}
    />
  );
};
